/**
 * Allocates a contiguous block of LexoRank `order` values for new or moved ArTable rows.
 *
 * Consumed by `useArTableActions` (`addRow`, `createBundle`, `moveBundleInManualOrder`,
 * `moveRowInManualOrder`, `pasteDownColumn`) and by `allocateMoveToBundleOrder`
 * (`moveToBundle`) as the single source of row ordering. The result has length `count`, ascends strictly, and stays
 * strictly inside `(floorOrder, ceilingOrder)` whenever those bounds are given.
 * With a ceiling every slot is bisected — `genPrev()`/`genNext()` are exact inverses in
 * lexorank, so a chained `genNext()` inside a bounded interval collides with the ceiling
 * (SYD-2186). A floor at or above the ceiling is clamped to `ceiling.genPrev()`; only a
 * `LexoRank.min()` ceiling leaves no interval, and that throws.
 */

import { LexoRank } from 'lexorank';

type AllocateRowOrdersParams = {
  floorOrder: string | null;
  ceilingOrder: string | null;
  count: number;
};

export function allocateRowOrders({ floorOrder, ceilingOrder, count }: AllocateRowOrdersParams): string[] {
  if (count <= 0) return [];

  const slots = Array.from({ length: count });

  if (ceilingOrder === null) {
    const seed = floorOrder !== null ? LexoRank.parse(floorOrder).genNext() : LexoRank.middle();
    return slots
      .reduce<LexoRank[]>((acc, _slot, index) => (index === 0 ? [seed] : [...acc, acc[index - 1].genNext()]), [])
      .map((rank) => rank.toString());
  }

  const ceilingRank = LexoRank.parse(ceilingOrder);
  const floorRank = floorOrder !== null && floorOrder < ceilingOrder
    ? LexoRank.parse(floorOrder)
    : ceilingRank.genPrev();

  if (floorRank.toString() >= ceilingOrder) {
    throw new Error(
      `allocateRowOrders: no order interval between floor ${floorRank.toString()} and ceiling ${ceilingOrder}`,
    );
  }

  return slots
    .reduce<LexoRank[]>((acc, _slot, index) => [
      ...acc,
      (index === 0 ? floorRank : acc[index - 1]).between(ceilingRank),
    ], [])
    .map((rank) => rank.toString());
}
